/*
 * ES Fascinante · CL-TSK-4277 · Barra fija de "Añadir" en la ficha (snippets/sticky-atc.liquid).
 * - No tiene formulario propio: pulsa el botón original, que hace el envío, publica los eventos
 *   y abre el carrito como siempre.
 * - Se sincroniza con el botón original y con el precio (el tema los re-renderiza al cambiar de
 *   variante), y con la talla marcada en el selector.
 * - Fija durante todo el scroll de la ficha.
 */
if (!customElements.get('esf-sticky-atc')) {
  customElements.define(
    'esf-sticky-atc',
    class EsfStickyAtc extends HTMLElement {
      connectedCallback() {
        // En el popup de añadir rápido no se usa
        if (this.closest('quick-add-modal')) return;

        // La columna de la ficha lleva una animación con transform, que rompe position: fixed:
        // la barra se mueve al body (al moverla vuelve a ejecutarse connectedCallback)
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }
        if (this.initialized) return;
        this.initialized = true;

        // Si el tema vuelve a pintar la ficha, solo queda una barra
        document.querySelectorAll('body > esf-sticky-atc').forEach((bar) => {
          if (bar !== this && bar.dataset.section === this.dataset.section) bar.remove();
        });

        const sectionId = this.dataset.section;
        this.mainButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
        this.buttonsWrapper = this.mainButton && this.mainButton.closest('.product-form__buttons');
        if (!this.mainButton || !this.buttonsWrapper) return;

        this.picker = document.getElementById(`variant-radios-${sectionId}`);
        this.priceEl = document.getElementById(`price-${sectionId}`);
        this.button = this.querySelector('[data-esf-sticky-button]');
        this.labelEl = this.querySelector('[data-esf-sticky-label]');
        this.priceOut = this.querySelector('[data-esf-sticky-price]');
        this.variantOut = this.querySelector('[data-esf-sticky-variant]');

        this.button.addEventListener('click', this.onClick.bind(this));
        this.update = this.update.bind(this);

        // El tema re-renderiza precio y botón de forma asíncrona tras cambiar la variante
        const observer = new MutationObserver(this.update);
        observer.observe(this.mainButton, { attributes: true, childList: true, subtree: true, characterData: true });
        if (this.priceEl) observer.observe(this.priceEl, { childList: true, subtree: true, characterData: true });
        if (this.picker) this.picker.addEventListener('change', () => setTimeout(this.update, 0));

        // Fija durante todo el scroll de la ficha (decisión de Adriana, 16/10/2026): no se oculta
        // aunque el botón original esté en pantalla. Solo se esconde con menús, carrito o ventanas (CSS)
        this.setVisible(true);

        document.body.classList.add('esf-sticky-atc-on');
        this.update();
      }

      setVisible(visible) {
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.button.tabIndex = visible ? 0 : -1;
        document.body.classList.toggle('esf-sticky-atc-visible', visible);
      }

      getSizeInputs() {
        return this.picker ? this.picker.querySelectorAll('[data-esf-size-option] input[type="radio"]') : [];
      }

      getCheckedSize() {
        return this.picker && this.picker.querySelector('[data-esf-size-option] input[type="radio"]:checked');
      }

      // Variante existente pero agotada (hay "Avísame") frente a combinación inexistente
      isSoldOutVariant() {
        // Al cargar, el selector del tema aún no ha calculado la variante actual
        if (this.picker && !this.picker.currentVariant && typeof this.picker.updateMasterId === 'function') {
          try {
            this.picker.updateOptions();
            this.picker.updateMasterId();
          } catch (e) {}
        }
        const variant = this.picker && this.picker.currentVariant;
        return Boolean(variant) && variant.available === false && Boolean(document.querySelector('.klaviyo-bis-trigger'));
      }

      update() {
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.dataset.section}`) || this.mainButton;
        // Talla elegida (o, si el producto no tiene talla, el nombre de la variante)
        const checkedSize = this.getCheckedSize();
        if (checkedSize) {
          this.variantOut.textContent = `${this.dataset.sizeLabel} ${checkedSize.value}`;
        } else if (this.dataset.hasOnlyDefault !== 'true' && this.picker && this.picker.currentVariant) {
          this.variantOut.textContent = this.picker.currentVariant.title;
        }

        // Precio actual tal como lo pinta la ficha (respeta mercado, moneda y rebaja)
        if (this.priceEl) {
          const onSale = this.priceEl.querySelector('.price--on-sale');
          const current = onSale
            ? this.priceEl.querySelector('.price__sale .price-item--sale')
            : this.priceEl.querySelector('.price__regular .price-item--regular');
          const text = current && current.textContent.trim();
          if (text) this.priceOut.textContent = text;
        }

        // Estado: disponible → "Añadir"; agotada → "Avísame"; inexistente → texto del botón original
        const disabled = this.mainButton.hasAttribute('disabled');
        this.classList.remove('is-soldout', 'is-disabled');
        if (!disabled) {
          this.labelEl.textContent = this.dataset.addLabel;
          this.button.disabled = false;
        } else if (this.isSoldOutVariant()) {
          this.labelEl.textContent = this.dataset.notifyLabel;
          this.button.disabled = false;
          this.classList.add('is-soldout');
        } else {
          const mainLabel = this.mainButton.querySelector('span');
          this.labelEl.textContent = (mainLabel && mainLabel.textContent.trim()) || this.dataset.addLabel;
          this.button.disabled = true;
          this.classList.add('is-disabled');
        }
      }

      onClick() {
        // Sin talla elegida: lleva al selector y lo resalta
        if (this.getSizeInputs().length && !this.getCheckedSize()) {
          this.focusPicker();
          return;
        }

        if (this.classList.contains('is-soldout')) {
          this.openNotify();
          return;
        }

        if (this.mainButton.hasAttribute('disabled') || this.mainButton.getAttribute('aria-disabled') === 'true') return;
        this.mainButton.click();
      }

      focusPicker() {
        if (!this.picker) return;
        this.picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.picker.classList.add('esf-picker-highlight');
        setTimeout(() => this.picker.classList.remove('esf-picker-highlight'), 1600);
      }

      openNotify() {
        // Mismo truco que el enlace "Avísame" del selector: el disparador de Klaviyo está oculto
        const trigger = document.querySelector('.klaviyo-bis-trigger');
        if (!trigger) return;
        const previousDisplay = trigger.style.display;
        trigger.style.display = 'block';
        trigger.click();
        trigger.style.display = previousDisplay;
      }
    }
  );
}
